import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ExpertShowcase } from "@/components/ExpertShowcase";
import { Testimonials } from "@/components/Testimonials";
import { FAQ } from "@/components/FAQ";
import { motion } from "framer-motion";
import {
  Sparkles, FileText, Users, Wand2, LayoutTemplate, Download, ArrowRight, Star, CheckCircle2, ChevronsRight, Briefcase, Upload, Gauge,
} from "lucide-react";

const INDUSTRIES = ["Technology", "Finance", "Healthcare", "Creative", "Consulting", "Marketing", "Engineering", "Product", "Design", "Data Science"];

const Landing = () => {
  const { user, login } = useAuth();

  const primaryCta = () => {
    if (user) window.location.href = "/dashboard";
    else login();
  };

  return (
    <div className="font-body">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 pt-20 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium mb-6">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> AI-powered resume writing
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Get ahead. <span className="text-primary">Five ways.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-lg">
              Build your resume with AI, upload your old resume to get a freshly designed one, check your ATS rating for free, talk one-on-one with industry experts, and find a job — all in one place.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={primaryCta} size="lg" className="rounded-full font-semibold text-base px-7 h-12" data-testid="hero-cta-btn">
                Build my resume <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Link to="/improve">
                <Button size="lg" className="rounded-full font-semibold text-base px-7 h-12 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="hero-improve-btn">
                  <Upload className="mr-1.5 h-4 w-4" /> Upload old resume
                </Button>
              </Link>
              <Link to="/ats-score">
                <Button variant="outline" size="lg" className="rounded-full font-semibold text-base px-7 h-12 border-primary text-primary hover:bg-primary/5" data-testid="hero-ats-btn">
                  <Gauge className="mr-1.5 h-4 w-4" /> Check ATS rating — free
                </Button>
              </Link>
              <Link to="/experts">
                <Button variant="outline" size="lg" className="rounded-full font-semibold text-base px-7 h-12" data-testid="hero-experts-btn">
                  <Users className="mr-1.5 h-4 w-4" /> Talk to an expert
                </Button>
              </Link>
              <Link to="/jobs">
                <Button variant="outline" size="lg" className="rounded-full font-semibold text-base px-7 h-12" data-testid="hero-jobs-btn">
                  <Briefcase className="mr-1.5 h-4 w-4" /> Find a job
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-muted-foreground max-w-lg" data-testid="hero-improve-note">
              Already have a resume? <span className="font-medium text-foreground">Upload it — no sign-in needed.</span> Get your free ATS rating instantly, or a free watermarked preview and pay only to download the clean version.
            </p>
            <div className="mt-8 flex items-center gap-5 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> Free to start</div>
              <div className="flex items-center gap-1.5"><Star className="h-4 w-4 text-accent fill-accent" /> 4.9 / 5 rating</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 relative"
          >
            <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-full" />
            <img
              src="https://images.pexels.com/photos/1181641/pexels-photo-1181641.jpeg"
              alt="Professional building a resume"
              className="relative rounded-2xl shadow-2xl object-cover w-full h-[420px] border border-border"
            />
          </motion.div>
        </div>

        {/* Marquee ribbon */}
        <div className="border-y border-border bg-secondary/50 py-3 overflow-hidden">
          <div className="marquee-track">
            {[...INDUSTRIES, ...INDUSTRIES].map((x, i) => (
              <span key={i} className="mx-6 text-sm font-medium text-muted-foreground uppercase tracking-wide">{x} ·</span>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-7xl mx-auto px-5 pt-24 pb-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary">Five ways to use ResumePro24</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mt-2">Pick your path</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Product 1 */}
          <div className="group relative rounded-3xl border border-border bg-card p-8 overflow-hidden hover:shadow-xl transition-shadow" data-testid="product-resume">
            <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">01 · Resume Writing</span>
            <h3 className="font-heading font-bold text-2xl mt-1.5">Write a winning resume</h3>
            <p className="text-muted-foreground mt-3">
              Generate powerful summaries and bullet points with AI, choose a template, and export a recruiter-ready PDF — or hand it to a professional writer.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> AI content generator (GPT-5.4)</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Live A4 preview & templates</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> One-click print / PDF</li>
            </ul>
            <Button onClick={primaryCta} size="lg" className="mt-7 rounded-full font-semibold" data-testid="product-resume-cta">
              Start building <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>

          {/* Product 2 - Upload & Improve */}
          <div className="group relative rounded-3xl border border-border bg-card p-8 overflow-hidden hover:shadow-xl transition-shadow" data-testid="product-improve">
            <div className="h-12 w-12 rounded-2xl bg-accent/15 flex items-center justify-center mb-5">
              <Upload className="h-6 w-6 text-accent" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">02 · Upload & Improve</span>
            <h3 className="font-heading font-bold text-2xl mt-1.5">Upload your old resume</h3>
            <p className="text-muted-foreground mt-3">
              Upload your existing resume and our AI redesigns it into a polished, ATS-ready version — no sign-in needed. Free watermarked preview, pay only to download the clean copy.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> AI rewrite + fresh design</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Free watermarked preview</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Pay only to download</li>
            </ul>
            <Link to="/improve">
              <Button size="lg" className="mt-7 rounded-full font-semibold bg-accent text-accent-foreground hover:bg-accent/90" data-testid="product-improve-cta">
                Upload old resume <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Product 3 - ATS Score */}
          <div className="group relative rounded-3xl border border-border bg-card p-8 overflow-hidden hover:shadow-xl transition-shadow" data-testid="product-ats">
            <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <Gauge className="h-6 w-6 text-primary" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">03 · ATS Score</span>
            <h3 className="font-heading font-bold text-2xl mt-1.5">Check your ATS rating</h3>
            <p className="text-muted-foreground mt-3">
              See how your resume scores against Applicant Tracking Systems out of 100 — instantly and completely free. Paste a job description for an accurate match.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Instant 0–100 score</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Match against any job</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> 100% free, no login</li>
            </ul>
            <Link to="/ats-score">
              <Button size="lg" className="mt-7 rounded-full font-semibold" data-testid="product-ats-cta">
                Check ATS rating <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Product 4 */}
          <div className="group relative rounded-3xl bg-[#0F172A] text-slate-100 p-8 overflow-hidden hover:shadow-xl transition-shadow" data-testid="product-experts">
            <div className="h-12 w-12 rounded-2xl bg-accent/20 flex items-center justify-center mb-5">
              <Users className="h-6 w-6 text-accent" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">04 · Talk to an Expert</span>
            <h3 className="font-heading font-bold text-2xl mt-1.5">Discuss your field's problems</h3>
            <p className="text-slate-300 mt-3">
              Book a call with the best minds in any industry — from a carbon black specialist to a banking & finance leader — and get real answers, one-on-one.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Experts across every industry</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Pick a time slot & book instantly</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Full profiles: photo, role & education</li>
            </ul>
            <Link to="/experts">
              <Button size="lg" className="mt-7 rounded-full font-semibold bg-white text-slate-900 hover:bg-slate-200" data-testid="product-experts-cta">
                Find an expert <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Product 3 */}
          <div className="group relative rounded-3xl border border-border bg-card p-8 overflow-hidden hover:shadow-xl transition-shadow" data-testid="product-jobs">
            <div className="h-12 w-12 rounded-2xl bg-accent/15 flex items-center justify-center mb-5">
              <Briefcase className="h-6 w-6 text-accent" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">05 · Job Finding Support</span>
            <h3 className="font-heading font-bold text-2xl mt-1.5">Find & apply for jobs</h3>
            <p className="text-muted-foreground mt-3">
              Browse open positions, apply with your resume, and let our team match you with companies actively hiring across every industry.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Browse & search open roles</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Apply for a job in one click</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> Get matched by our hiring team</li>
            </ul>
            <Link to="/jobs">
              <Button size="lg" className="mt-7 rounded-full font-semibold" data-testid="product-jobs-cta">
                Apply for a job <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <ExpertShowcase />

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-5 py-24">
        <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight max-w-2xl">
          Everything you need to write a standout resume.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 rounded-2xl border border-border bg-card p-8 hover:shadow-lg transition-shadow">
            <Wand2 className="h-8 w-8 text-primary mb-4" />
            <h3 className="font-heading font-semibold text-2xl mb-2">AI content generator</h3>
            <p className="text-muted-foreground max-w-md">Turn a few notes into powerful, quantified bullet points and summaries. Beat the applicant tracking systems automatically.</p>
            <img src="https://images.pexels.com/photos/590044/pexels-photo-590044.jpeg" alt="Resume desk" className="mt-6 rounded-xl w-full h-52 object-cover" />
          </div>
          <div className="md:col-span-5 grid gap-6">
            <div className="rounded-2xl border border-border bg-card p-8 hover:shadow-lg transition-shadow">
              <LayoutTemplate className="h-8 w-8 text-accent mb-4" />
              <h3 className="font-heading font-semibold text-xl mb-2">Beautiful templates</h3>
              <p className="text-muted-foreground text-sm">Modern, classic and minimal layouts with a live A4 preview as you type.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-8 hover:shadow-lg transition-shadow">
              <Download className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-heading font-semibold text-xl mb-2">Instant print / PDF</h3>
              <p className="text-muted-foreground text-sm">Export a clean, recruiter-ready document in one click.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Expert CTA - dark */}
      <section className="bg-[#0F172A] text-slate-100">
        <div className="max-w-7xl mx-auto px-5 py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-xs font-medium mb-6">
              <Users className="h-3.5 w-3.5 text-accent" /> Work with a professional
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">
              Prefer a human touch? Book a resume expert.
            </h2>
            <p className="mt-4 text-slate-300 max-w-lg">
              Our vetted career coaches and recruiters review your resume line by line, tailored to your target role and industry.
            </p>
            <Link to="/experts">
              <Button size="lg" className="mt-8 rounded-full font-semibold bg-white text-slate-900 hover:bg-slate-200" data-testid="expert-cta-btn">
                Browse experts <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg",
              "https://images.pexels.com/photos/27086922/pexels-photo-27086922.jpeg",
              "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODc3NDkyNjZ8MA&ixlib=rb-4.1.0&q=85",
            ].map((src, i) => (
              <img key={i} src={src} alt="Expert" className={`rounded-xl object-cover h-56 w-full border border-slate-700 ${i === 1 ? "mt-8" : ""}`} />
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      <FAQ />

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-5 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-primary flex items-center justify-center">
              <FileText className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold flex items-center">
              ResumePro24.com
              <ChevronsRight className="h-4 w-4 text-accent ml-0.5" strokeWidth={3} />
            </span>
          </div>
          <p className="text-sm text-muted-foreground">© 2026 ResumePro24. Craft your next opportunity.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
